import { useDataGridConfig } from '@/hooks/useDataGridConfig'
import http from '@/lib/http'
import { u } from '@/lib/url'
import type { UserListItem } from '@/types/user'
import { keepPreviousData, useQuery, type UseQueryOptions } from '@tanstack/react-query'
import { useMemo } from 'react'
import type { GridFilterItem, GridSortModel } from '@mui/x-data-grid'
import { buildFilterParams, type FilterFieldMapping, type SortFieldMapping } from '@/lib/dataGridFilters'
import type { OrderListItem } from '@/types/order'
import { fetchOrders } from './orders'

// GridSortItem is not exported from @mui/x-data-grid, so we extract it from GridSortModel
type GridSortItem = GridSortModel[number]

/**
 * Field mappings for user DataGrid filters.
 * Maps DataGrid column fields to backend filter parameter names.
 */
export const USER_FILTER_FIELD_MAPPING: FilterFieldMapping = {
  displayName: { paramName: 'displayNameFilter', type: 'text' },
  email: { paramName: 'emailFilter', type: 'text' },
  phone: { paramName: 'phoneFilter', type: 'text' },
  registeredAt: { paramName: 'registeredAtFilter', type: 'date' }
}

/**
 * Field mappings for user DataGrid sorting.
 * Maps DataGrid column fields to backend sort field names.
 */
export const USER_SORT_FIELD_MAPPING: SortFieldMapping = {
  displayName: 'display_name',
  email: 'email',
  phone: 'phone',
  registeredAt: 'registered_at'
}

export type FetchUsersParams = {
  searchQuery?: string
  searchMode?: 'contains' | 'startsWith'
  /** JSON-stringified array of search attributes, e.g. `JSON.stringify(['email','display_name'])`. */
  searchAttributes?: string
  /** When true, skip the total COUNT query. Response `count` reflects only returned rows. */
  skipCount?: boolean
  skip?: number
  limit?: number

  // Advanced filters (JSON-encoded)
  displayNameFilter?: string
  emailFilter?: string
  phoneFilter?: string
  registeredAtFilter?: string

  // Sorting
  sortBy?: 'display_name' | 'email' | 'phone' | 'registered_at'
  sortByDirection?: 'asc' | 'desc'
}

export type FetchUsersResponse = {
  users: UserListItem[]
  count: number
}

// API response type (snake_case from backend)
type APIUser = {
  hash: string
  nice_name: string
  email: string
  phone: string
  display_name: string
  registered_at: string
}

type APIResponse = {
  users: APIUser[]
  count: number
}

/**
 * Transform API user (snake_case) to frontend UserListItem (camelCase)
 */
function toUserListItem(apiUser: APIUser): UserListItem {
  return {
    id: apiUser.hash,
    niceName: apiUser.nice_name,
    email: apiUser.email,
    phone: apiUser.phone,
    displayName: apiUser.display_name,
    registeredAt: apiUser.registered_at
  }
}

/**
 * Fetch users with optional search, filters, and pagination
 * @returns Paginated list of users with total count
 */
export const fetchUsers = async (params: FetchUsersParams = {}, signal?: AbortSignal): Promise<FetchUsersResponse> => {
  const { users, count } = (await http.get('/users', {
    params,
    signal
  })) as APIResponse

  return {
    users: users.map(toUserListItem),
    count: count
  }
}

export type UseUsersQueryOptions = {
  tableId?: string
  enabled?: boolean
  /** When provided, bypasses DataGrid config and uses these params directly (for compact/search mode). */
  params?: Pick<
    FetchUsersParams,
    'searchQuery' | 'searchMode' | 'searchAttributes' | 'limit' | 'skip' | 'emailFilter' | 'phoneFilter' | 'skipCount'
  >
}

/**
 * Hook to fetch users with DataGrid integration.
 * Supports search query, column filters, sorting, and pagination.
 *
 * When `params` is provided, bypasses DataGrid config (pagination, filters, sorting)
 * and queries directly with the given params — useful for dashboard search.
 */
export const useUsersQuery = ({
  tableId = 'users',
  enabled = true,
  params: overrideParams
}: UseUsersQueryOptions = {}) => {
  const dataGridConfig = useDataGridConfig({ tableId, syncToUrl: !overrideParams })

  const queryParams: FetchUsersParams = useMemo(() => {
    if (overrideParams) return overrideParams

    const { paginationModel, filters, filterModel, sortModel } = dataGridConfig

    const params: FetchUsersParams = {
      skip: paginationModel ? paginationModel.page * paginationModel.pageSize : 0,
      limit: paginationModel?.pageSize ?? 10
    }

    const filterParams = buildFilterParams({
      searchQuery: filterModel?.quickFilterValues?.join(' '),
      filterItems: filters as GridFilterItem[],
      sortItems: (sortModel ?? []) as GridSortItem[],
      filterFieldMapping: USER_FILTER_FIELD_MAPPING,
      sortFieldMapping: USER_SORT_FIELD_MAPPING
    })

    // Match the dashboard's general search, so a "View all" from a name hit lands on the same users
    // instead of the backend-default email-only search.
    const searchAugment: Pick<FetchUsersParams, 'searchAttributes'> = filterParams.searchQuery
      ? { searchAttributes: JSON.stringify(['email', 'display_name']) }
      : {}

    return { ...params, ...filterParams, ...searchAugment }
  }, [overrideParams, dataGridConfig])

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ['users', queryParams],
    queryFn: ({ signal }) => fetchUsers(queryParams, signal),
    enabled,
    placeholderData: keepPreviousData
  })

  const users = useMemo(
    () =>
      (data?.users ?? []).map(user => ({
        ...user,
        registeredAt: user.registeredAt ? new Date(user.registeredAt) : null
      })),
    [data?.users]
  )

  const count = queryParams.skipCount ? users.length : (data?.count ?? 0)

  return {
    users,
    count,
    isLoading,
    isFetching,
    isError,
    error,
    dataGridConfig
  }
}

export type APIUserAddress = {
  hash: string
  street: string
  city: string
  postCode: string
  state: string
  lastUsed: string
}

export type APIUserPatient = {
  hash: string
  firstName: string
  lastName: string
  dob: string
  gender: string
  idVerified: boolean
  isPotentialDuplicate?: boolean
}

// API response type for single user endpoint
export type APIUserDetail = {
  hash: string
  firstName: string
  lastName: string
  email: string
  mobile: string
  ssoProvider?: string
  // billingAddresses: Array<APIUserAddress>
  // shippingAddresses: Array<APIUserAddress>
  patients: Array<APIUserPatient>
}

/**
 * Fetch a single user by email with all related data
 * @param email - The user's email address
 * @returns User with patients, orders, and addresses
 */
export const fetchUserByEmail = async (email: string): Promise<APIUserDetail> => {
  const user = (await http.get(u`/users/${email}`)) as APIUserDetail
  return user
}

/**
 * Suspense query hook for fetching a single user by email.
 * Throws a promise while loading (for Suspense boundaries).
 * @param email - The user's email address
 */
export const useUserByEmailQuery = (email: string) => {
  return useQuery<APIUserDetail>({
    queryKey: ['user', email],
    queryFn: () => fetchUserByEmail(email)
  })
}

/**
 * React Query hook to fetch all orders for a specific user.
 * @param email - The user's email address to filter orders by
 * @param options - Optional React Query options to customize the query behavior
 * @returns Query result containing an array of OrderListItem objects
 */
export const useUserOrdersQuery = (email: string, options?: Partial<UseQueryOptions<OrderListItem[]>>) => {
  return useQuery<OrderListItem[]>({
    queryKey: ['user-orders', email],
    queryFn: async () => {
      const data = await fetchOrders({
        userEmailFilter: JSON.stringify({
          operator: 'equals',
          value: email
        })
      })

      const { orders } = data
      return orders
    },
    ...options
  })
}
